import type { ChatAttachment, ChatMessage } from "@/lib/chatApi";
import { isImageAttachment } from "@/lib/chatFormat";
import { extractLoneMediaUrl } from "@/components/ChatEmojiGifPicker";
import { chatSuiteFetch } from "@/lib/suite/chatSuiteFetch";

export type SaveChatResult = { ok: boolean; error?: string };

export function collectMessageSaveTargets(m: ChatMessage): ChatAttachment[] {
  const out: ChatAttachment[] = [...(m.attachments ?? [])];
  const lone = extractLoneMediaUrl(m.body || "");
  if (lone && !out.some((a) => a.url === lone)) {
    out.push({ url: lone, name: filenameFromUrl(lone), mime: "image/gif" });
  }
  return out.filter((a) => Boolean(a.url));
}

export function filenameFromUrl(url: string): string {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.href : "https://local/");
    const last = u.pathname.split("/").filter(Boolean).pop() || "fichier";
    return decodeURIComponent(last);
  } catch {
    return "fichier";
  }
}

const blobCache = new Map<string, Blob>();
const blobInflight = new Map<string, Promise<Blob>>();

export async function fetchChatAttachmentBlob(att: ChatAttachment): Promise<Blob> {
  const hit = blobCache.get(att.url);
  if (hit) return hit;
  const pending = blobInflight.get(att.url);
  if (pending) return pending;
  const job = (async () => {
    const res = await fetch(att.url, { credentials: "include", cache: "no-store" });
    if (!res.ok) {
      throw new Error("Impossible de lire le fichier.");
    }
    const blob = await res.blob();
    blobCache.set(att.url, blob);
    blobInflight.delete(att.url);
    return blob;
  })();
  blobInflight.set(att.url, job);
  return job;
}

export function prefetchChatAttachment(att: ChatAttachment): void {
  if (!att.url) return;
  void fetchChatAttachmentBlob(att).catch(() => {
    /* préchargement best-effort */
  });
}

export function cachedChatFile(att: ChatAttachment): File | null {
  const blob = blobCache.get(att.url);
  if (!blob) return null;
  const name = att.name || filenameFromUrl(att.url);
  return new File([blob], name, { type: blob.type || att.mime || "application/octet-stream" });
}

function isIosLike(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function triggerBlobDownload(file: File) {
  const href = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = href;
  a.download = file.name || "fichier";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 2500);
}

function canShareFile(file: File): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  );
}

/** iOS PWA : share (Enregistrer l’image). Ailleurs : vrai fichier, comme Mail. */
export function shareOrDownloadFile(file: File): void {
  if (isIosLike() && canShareFile(file)) {
    void navigator.share({ files: [file], title: file.name }).catch((err: unknown) => {
      if (err instanceof Error && err.name === "AbortError") return;
      triggerBlobDownload(file);
    });
    return;
  }
  triggerBlobDownload(file);
}

export async function downloadChatAttachment(att: ChatAttachment): Promise<SaveChatResult> {
  const name = att.name || filenameFromUrl(att.url);
  try {
    const ready = cachedChatFile(att);
    if (ready) {
      shareOrDownloadFile(ready);
      return { ok: true };
    }
    const blob = await fetchChatAttachmentBlob(att);
    const file = new File([blob], name, { type: blob.type || att.mime || "application/octet-stream" });
    shareOrDownloadFile(file);
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Téléchargement impossible.",
    };
  }
}

export async function downloadChatAttachments(atts: ChatAttachment[]): Promise<SaveChatResult> {
  let failed = 0;
  for (const att of atts) {
    const r = await downloadChatAttachment(att);
    if (!r.ok) failed += 1;
  }
  if (failed === 0) return { ok: true };
  if (failed === atts.length) return { ok: false, error: "Téléchargement impossible." };
  return { ok: false, error: `${failed} fichier(s) non téléchargé(s).` };
}

export async function saveChatAttachmentToCoffre(
  att: ChatAttachment,
  folderPath: string
): Promise<SaveChatResult> {
  try {
    const blob = await fetchChatAttachmentBlob(att);
    const form = new FormData();
    form.append("path", folderPath);
    form.append("file", blob, att.name || filenameFromUrl(att.url));
    form.append("zone", "vault");
    const res = await chatSuiteFetch<{ message?: string; error?: string }>("coffre-upload.php", {
      method: "POST",
      body: form,
    });
    if (!res.ok) {
      const locked =
        res.status === 403 &&
        (res.data.error === "coffre_locked" ||
          res.data.error === "coffre_session_expired" ||
          (res.data.message ?? "").toLowerCase().includes("déverrouill"));
      return {
        ok: false,
        error:
          res.data.message ??
          (locked
            ? "Déverrouillez le Coffre avec votre code d’accès avant d’enregistrer."
            : "Échec Coffre."),
      };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Enregistrement Coffre impossible.",
    };
  }
}

export async function saveChatAttachmentToPhotos(
  att: ChatAttachment,
  folderPath: string
): Promise<SaveChatResult> {
  if (!isImageAttachment(att)) {
    return { ok: false, error: "Format non image." };
  }
  try {
    const blob = await fetchChatAttachmentBlob(att);
    const form = new FormData();
    form.append("path", folderPath);
    form.append("file", blob, att.name || filenameFromUrl(att.url));
    const res = await chatSuiteFetch<{ message?: string }>("photos-upload.php", {
      method: "POST",
      body: form,
    });
    if (!res.ok) {
      return { ok: false, error: res.data.message ?? "Échec Photos." };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Enregistrement Photos impossible.",
    };
  }
}

export async function saveChatAttachmentsToCoffre(
  atts: ChatAttachment[],
  folderPath: string
): Promise<SaveChatResult> {
  let ok = 0;
  let lastErr = "";
  for (const att of atts) {
    const r = await saveChatAttachmentToCoffre(att, folderPath);
    if (r.ok) ok += 1;
    else lastErr = r.error || lastErr;
  }
  if (ok === atts.length) return { ok: true };
  if (ok === 0) return { ok: false, error: lastErr || "Échec Coffre." };
  return { ok: false, error: `${ok}/${atts.length} enregistré(s). ${lastErr}` };
}

export async function saveChatAttachmentsToPhotos(
  atts: ChatAttachment[],
  folderPath: string
): Promise<SaveChatResult> {
  const images = atts.filter(isImageAttachment);
  if (images.length === 0) return { ok: false, error: "Aucune image à enregistrer." };
  let ok = 0;
  let lastErr = "";
  for (const att of images) {
    const r = await saveChatAttachmentToPhotos(att, folderPath);
    if (r.ok) ok += 1;
    else lastErr = r.error || lastErr;
  }
  if (ok === images.length) return { ok: true };
  if (ok === 0) return { ok: false, error: lastErr || "Échec Photos." };
  return { ok: false, error: `${ok}/${images.length} image(s). ${lastErr}` };
}

export async function fetchCoffreUnlocked(): Promise<{ ok: boolean; unlocked: boolean; message?: string }> {
  const res = await chatSuiteFetch<{
    success?: boolean;
    server_unlocked?: boolean | number | string;
    message?: string;
    error?: string;
  }>(`coffre-status.php`);
  const unlocked =
    res.data.server_unlocked === true ||
    res.data.server_unlocked === 1 ||
    res.data.server_unlocked === "1";
  return {
    ok: res.ok,
    unlocked: res.ok && unlocked,
    message: res.data.message ?? res.data.error,
  };
}

export async function fetchCoffreFolders(
  path = ""
): Promise<{ path: string; folders: Array<{ name: string; path: string }>; error?: string }> {
  const qs = new URLSearchParams();
  if (path) qs.set("path", path);
  qs.set("zone", "picker");
  qs.set("folders_only", "1");
  const res = await chatSuiteFetch<{
    path?: string;
    entries?: Array<{ name: string; path: string; kind?: string }>;
    message?: string;
    error?: string;
  }>(`coffre-list.php?${qs.toString()}`);
  if (!res.ok) {
    return {
      path,
      folders: [],
      error: res.data.message ?? res.data.error ?? "Impossible de lire le Coffre.",
    };
  }
  const folders = (res.data.entries ?? [])
    .filter((e) => e.kind === "folder" || e.kind === "shortcut")
    .map((e) => ({ name: e.name, path: e.path }));
  return { path: res.data.path ?? path, folders };
}

export async function fetchPhotosFolders(
  path = ""
): Promise<{ path: string; folders: Array<{ name: string; path: string }>; error?: string }> {
  const qs = new URLSearchParams();
  qs.set("view", "folder");
  if (path) qs.set("path", path);
  const res = await chatSuiteFetch<{
    path?: string;
    folders?: Array<{ name: string; path: string }>;
    message?: string;
  }>(`photos-list.php?${qs.toString()}`);
  if (!res.ok) {
    return {
      path,
      folders: [],
      error: res.data.message ?? "Impossible de lire Photos.",
    };
  }
  return { path: res.data.path ?? path, folders: res.data.folders ?? [] };
}
